import type { ClassAttributes, HTMLAttributes } from 'react';
import { useIntl } from 'react-intl';
import type { Availability } from '@packages/gql/generated/shopping/graphql';
import type { EcoFriendlyDeliveryProps } from '../EcoFriendlyDelivery/EcoFriendlyDelivery';
import { EcoFriendlyDelivery } from '../EcoFriendlyDelivery/EcoFriendlyDelivery';
import { availabilityInfoMessages } from './messages';
import { AvailabilityInfoIndicator } from '../AvailabilityInfoIndicator/AvailabilityInfoIndicator';

const contentWrapperDefaultClasses: HTMLAttributes<HTMLDivElement>['className'] =
  'flex flex-row flex-nowrap gap-1';

export type AvailabilityInfoProps = ClassAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    availabilityMessage: Availability['message'];
    availabilityState: Availability['state'];
    serviceLayer?: EcoFriendlyDeliveryProps['serviceLayer'];
  };

/**
 * AvailabilityInfo component displays product availability
 * */
export const AvailabilityInfo = ({
  availabilityMessage,
  availabilityState,
  serviceLayer,
  ...props
}: AvailabilityInfoProps) => {
  const { formatMessage } = useIntl();

  let message = '';
  let bold = false;
  if (availabilityState === 'UNSELECTED') {
    message = formatMessage(availabilityInfoMessages.deliveryMessageUnselected);
  } else if (availabilityMessage.length === 0) {
    message = formatMessage(availabilityInfoMessages.deliveryMessageFallback);
  } else {
    message = availabilityMessage;
    bold = true;
  }
  const defaultContent = (
    <>
      <AvailabilityInfoIndicator type={availabilityState} />
      <div className={`text-size-body2 ${bold && 'font-bold'}`}>{message}</div>
    </>
  );

  if (serviceLayer && availabilityState !== 'UNSELECTED') {
    return (
      <div {...props} className="flex flex-col flex-nowrap gap-0.5">
        <div className={`${contentWrapperDefaultClasses} items-center`}>{defaultContent}</div>
        <div className={contentWrapperDefaultClasses}>
          <AvailabilityInfoIndicator type="PLACEHOLDER" />
          <div>
            <EcoFriendlyDelivery serviceLayer={serviceLayer} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div {...props} className={`${contentWrapperDefaultClasses} items-center`}>
      {defaultContent}
    </div>
  );
};
